import { useEffect, useRef, useState } from "react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import {
  createLocalId,
  validateImageFile,
} from "./product-images/helpers";

/** A file collected locally, before the product exists to upload against. */
export type LocalProductImage = {
  id: string;
  file: File;
  previewUrl: string;
};

type Props = {
  images: LocalProductImage[];
  onChange: (next: LocalProductImage[]) => void;
  maxImages: number;
  disabled?: boolean;
};

/**
 * Inline image collector for the create-product drawer — the dashed dropzone
 * from the design, sitting at the top of the single create form. It holds files
 * *locally* (the product doesn't exist yet); the drawer uploads them right after
 * the product is created. Full image management (reorder, cover, bg-removal)
 * stays on the Edit Product page.
 */
export function ProductCreateImagePicker({
  images,
  onChange,
  maxImages,
  disabled,
}: Props) {
  const t = useTranslations();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Revoke any object URLs still held when the picker unmounts. The ref is kept
  // in sync via an effect (writing it during render is disallowed) so the
  // unmount cleanup always sees the latest set.
  const imagesRef = useRef(images);
  useEffect(() => {
    imagesRef.current = images;
  }, [images]);
  useEffect(
    () => () => {
      for (const img of imagesRef.current) URL.revokeObjectURL(img.previewUrl);
    },
    [],
  );

  const limitReached = images.length >= maxImages;

  const addFiles = async (files: FileList | File[]) => {
    setError(null);
    const list = Array.from(files);
    const next = [...images];
    for (const file of list) {
      if (next.length >= maxImages) break;
      try {
        await validateImageFile(file);
        next.push({
          id: createLocalId(),
          file,
          previewUrl: URL.createObjectURL(file),
        });
      } catch (caught) {
        const reason = caught instanceof Error ? caught.message : "invalidImage";
        setError(
          reason === "fileTooLarge"
            ? t(keys.errors.fileTooLarge)
            : t(keys.products.uploadFailed),
        );
      }
    }
    onChange(next);
  };

  const remove = (id: string) => {
    const target = images.find((img) => img.id === id);
    if (target) URL.revokeObjectURL(target.previewUrl);
    onChange(images.filter((img) => img.id !== id));
  };

  return (
    <div className="flex flex-col gap-1.5">
      <span className="flex items-center gap-1.5 text-[12px] font-medium leading-tight text-[color:var(--label-ink)]">
        {t(keys.products.imagesTitle)}
        <span className="rounded-full border border-[color:var(--border-light)] bg-[color:var(--surface-alt)] px-2 py-px text-[11px] font-semibold text-[color:var(--text-muted)]">
          {t(keys.common.optional)}
        </span>
      </span>

      {images.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {images.map((img) => (
            <div
              key={img.id}
              className="group relative h-16 w-16 overflow-hidden rounded-[10px] border border-[color:var(--card-br,var(--border-light))] bg-[color:var(--surface-alt)]"
            >
              <img
                src={img.previewUrl}
                alt=""
                className="h-full w-full object-cover"
              />
              <button
                type="button"
                disabled={disabled}
                onClick={() => remove(img.id)}
                aria-label={t(keys.products.removeImage)}
                className="absolute end-1 top-1 flex h-5 w-5 items-center justify-center rounded-md bg-[color:var(--surface)]/90 text-[color:var(--text-muted)] shadow-sm transition-colors hover:text-[color:var(--danger-500,var(--error-500))]"
              >
                <XIcon />
              </button>
            </div>
          ))}
        </div>
      ) : null}

      {!limitReached ? (
        <div
          role="button"
          tabIndex={disabled ? -1 : 0}
          aria-disabled={disabled}
          onClick={() => !disabled && inputRef.current?.click()}
          onKeyDown={(e) => {
            if (!disabled && (e.key === "Enter" || e.key === " ")) {
              e.preventDefault();
              inputRef.current?.click();
            }
          }}
          onDragOver={(e) => {
            e.preventDefault();
            if (!disabled) setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            if (!disabled && e.dataTransfer.files.length) {
              void addFiles(e.dataTransfer.files);
            }
          }}
          className={`flex cursor-pointer flex-col items-center gap-[7px] rounded-[12px] border-[1.5px] border-dashed p-[18px] text-center transition-colors ${
            dragOver
              ? "border-[color:var(--accent-500)] bg-[color:var(--hover,var(--surface-alt))]"
              : "border-[color:var(--input-br,var(--border-light))] bg-[color:var(--surface-alt)]"
          } ${disabled ? "cursor-not-allowed opacity-60" : ""}`}
        >
          <span className="flex h-[38px] w-[38px] items-center justify-center rounded-[10px] border border-[color:var(--card-br,var(--border-light))] bg-[color:var(--surface)] text-[color:var(--text-muted)]">
            <PlusIcon />
          </span>
          <span className="text-[12.5px] text-[color:var(--text-secondary)]">
            <span className="font-bold text-[color:var(--text-primary)]">
              {t(keys.products.emptyUploadHint)}
            </span>{" "}
            {t(keys.products.imageDragHint)}
          </span>
          <span className="text-[11px] text-[color:var(--text-muted)]">
            {t(keys.products.imageUploadHint)}
          </span>
        </div>
      ) : null}

      {error ? (
        <span className="text-[11px] leading-snug text-[color:var(--error-500)]">
          {error}
        </span>
      ) : (
        <span className="flex items-center gap-1.5 text-[11px] leading-snug text-[color:var(--text-muted)]">
          <BulbIcon />
          {[
            t(keys.products.imageTipLight),
            t(keys.products.imageTipBackground),
            t(keys.products.imageTipAngles),
          ].join(" · ")}
        </span>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        multiple
        hidden
        onChange={(e) => {
          if (e.target.files?.length) void addFiles(e.target.files);
          e.target.value = "";
        }}
      />
    </div>
  );
}

function PlusIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  );
}

function BulbIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="shrink-0"
    >
      <path d="M9 18h6M10 22h4M12 2a7 7 0 0 0-4 12.7c.6.5 1 1.3 1 2.1h6c0-.8.4-1.6 1-2.1A7 7 0 0 0 12 2Z" />
    </svg>
  );
}
